import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { CampoControlado } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { dinheiro, simboloMoeda } from '../../nucleo/formato';
import { exemploDinheiroEm } from '../../nucleo/mascaras';
import { useContasFin, useFormasPagamento } from './dados';
import { contaSugeridaNaBaixa, hojeLocal, invalidarContas, type Documento, type Encargos } from './tipos';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { TextoCortado } from '../../componentes/texto-cortado';
import { rotuloNomeForma } from '../../nucleo/forma-pagamento';

/**
 * Baixa parcial ou total (M5.2). Juros e multa vêm calculados pelo atraso na data escolhida; quem preferir pode digitar outro valor.
 * A chave de idempotência nasce com a tela e muda só depois de uma baixa feita: reenviar por engano não baixa duas vezes.
 */
export function ModalBaixa({ doc, aoFechar }: { doc: Documento | null; aoFechar: () => void }) {
  if (!doc) return null;
  return <Corpo doc={doc} aoFechar={aoFechar} />;
}

function Corpo({ doc, aoFechar }: { doc: Documento; aoFechar: () => void }) {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const contas = useContasFin();
  const [chave] = useState(() => crypto.randomUUID());
  const [valor, setValor] = useState<number | null>(doc.saldo_centavos);
  const [data, setData] = useState(hojeLocal());
  const [contaEscolhida, setContaEscolhida] = useState('');
  // Lote 09/10 (C3): sem escolha, vem a conta prevista do documento (a conta bancária da compra), senão a padrão.
  // rl0411-1 (SPEC-B3): a baixa é na moeda do título; conta de outra moeda só daria 422.
  const contasDaMoeda = (contas.data?.itens ?? []).filter((c) => !c.moeda || c.moeda === doc.moeda);
  const contaId = contaEscolhida || contaSugeridaNaBaixa(doc, contasDaMoeda);
  // rl0411-2 (SPEC-B2, VIS-B4): sem conta na moeda do título, diz o que falta em vez de deixar o servidor recusar.
  const semContaNaMoeda = !!contas.data && !contasDaMoeda.length;
  const [juros, setJuros] = useState<number | null>(null);
  const [multa, setMulta] = useState<number | null>(null);
  const [desconto, setDesconto] = useState<number | null>(null);
  const [tarifa, setTarifa] = useState<number | null>(null);
  // Fase 2 (W24, K9, P4): vazio vale a forma do documento (o servidor herda); escolher outra grava a usada de fato.
  const formas = useFormasPagamento();
  const [forma, setForma] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  useValidacaoLocal(null, erro);

  const valido = !!valor && valor > 0 && valor <= doc.saldo_centavos && /^\d{4}-\d{2}-\d{2}$/.test(data);
  const previa = useQuery({
    queryKey: ['/contas/documentos', 'encargos', doc.id, valor, data],
    queryFn: () => get<Encargos>(`/contas/documentos/${doc.id}/encargos`, { valor_centavos: valor, data }),
    enabled: valido,
  });
  const jurosFinal = juros ?? previa.data?.juros_centavos ?? 0;
  const multaFinal = multa ?? previa.data?.multa_centavos ?? 0;
  const total = (valor ?? 0) + jurosFinal + multaFinal - (desconto ?? 0);
  const receber = doc.direcao === 'receber';

  const baixar = useMutation({
    mutationFn: () =>
      post(
        `/contas/documentos/${doc.id}/baixar`,
        {
          valor_centavos: valor,
          data,
          conta_id: contaId || null,
          juros_centavos: juros ?? undefined,
          multa_centavos: multa ?? undefined,
          desconto_centavos: desconto || undefined,
          tarifa_centavos: tarifa || undefined,
          forma_pagamento_id: forma || undefined,
        },
        { 'Idempotency-Key': chave },
      ),
    onSuccess: async () => {
      await invalidarContas(qc);
      toast.sucesso(t('mensagens.baixado'));
      aoFechar();
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });

  const aviso = !valor ? t('baixa.valorObrigatorio') : valor > doc.saldo_centavos ? t('baixa.acimaSaldo', { saldo: dinheiro(doc.saldo_centavos, doc.moeda) }) : null;
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('baixa.titulo', { numero: doc.numero, context: receber ? 'receber' : 'pagar' })}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!valido || total <= 0 || semContaNaMoeda} carregando={baixar.isPending} onClick={() => (setErro(null), baixar.mutate())}>
            {receber ? t('baixa.confirmarReceber') : t('baixa.confirmarPagar')}
          </Botao>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <p className="text-sm text-tinta-2 sm:col-span-2">{t('baixa.saldo', { saldo: dinheiro(doc.saldo_centavos, doc.moeda) })}</p>
        <CampoControlado def={{ nome: 'valor', rotulo: t('baixa.valor'), tipo: 'dinheiro', placeholder: exemploDinheiroEm(simboloMoeda(doc.moeda)), obrigatorio: true }} valor={valor} aoMudar={(v) => setValor(v as number | null)} erro={aviso ?? undefined} />
        <CampoControlado def={{ nome: 'data', rotulo: t('baixa.data'), tipo: 'data', obrigatorio: true }} valor={data} aoMudar={(v) => setData(String(v ?? ''))} />
        <div className="sm:col-span-2">
          <CampoControlado
            def={{ nome: 'conta', rotulo: t('baixa.conta'), tipo: 'selecao', obrigatorio: true, opcoes: contasDaMoeda.map((c) => ({ valor: c.id, rotulo: c.padrao ? `${c.nome} (${t('baixa.padrao')})` : c.nome })), ajuda: semContaNaMoeda ? t('baixa.semContaNaMoeda', { moeda: doc.moeda }) : t('baixa.contaAjuda') }}
            valor={contaId}
            aoMudar={(v) => setContaEscolhida(String(v ?? ''))}
          />
        </div>
        {formas.habilitado && (
          <div className="sm:col-span-2">
            <CampoControlado
              def={{
                nome: 'forma_pagamento_id',
                rotulo: t('w24.formaPagamento'),
                tipo: 'selecao',
                placeholder: doc.forma_pagamento_nome ? t('w24.formaDoDocumento', { nome: rotuloNomeForma(doc.forma_pagamento_nome) }) : t('w24.semForma'),
                opcoes: formas.opcoes.filter((o) => o.valor !== doc.forma_pagamento_id),
                ajuda: t('w24.ajudaFormaBaixa'),
              }}
              valor={forma}
              aoMudar={(v) => setForma(String(v ?? ''))}
            />
          </div>
        )}
        {previa.data && previa.data.dias_atraso > 0 && (
          <p className="rounded-lg border border-laranja px-3 py-2 text-sm text-laranja sm:col-span-2">{t('baixa.atraso', { count: previa.data.dias_atraso })}</p>
        )}
        <CampoControlado def={{ nome: 'juros', rotulo: t('baixa.juros'), tipo: 'dinheiro', placeholder: exemploDinheiroEm(simboloMoeda(doc.moeda)), ajuda: t('baixa.calculado', { valor: dinheiro(previa.data?.juros_centavos ?? 0, doc.moeda) }) }} valor={juros} aoMudar={(v) => setJuros(v as number | null)} />
        <CampoControlado def={{ nome: 'multa', rotulo: t('baixa.multa'), tipo: 'dinheiro', placeholder: exemploDinheiroEm(simboloMoeda(doc.moeda)), ajuda: t('baixa.calculado', { valor: dinheiro(previa.data?.multa_centavos ?? 0, doc.moeda) }) }} valor={multa} aoMudar={(v) => setMulta(v as number | null)} />
        <CampoControlado def={{ nome: 'desconto', rotulo: t('baixa.desconto'), tipo: 'dinheiro', placeholder: exemploDinheiroEm(simboloMoeda(doc.moeda)), ajuda: t('baixa.descontoAjuda') }} valor={desconto} aoMudar={(v) => setDesconto(v as number | null)} />
        {/* Fase 2, W7 (F8): a tarifa do banco vira um lançamento próprio de despesa financeira. */}
        <CampoControlado def={{ nome: 'tarifa', rotulo: t('w7.tarifaBaixa'), tipo: 'dinheiro', placeholder: exemploDinheiroEm(simboloMoeda(doc.moeda)), ajuda: t('w7.tarifaAjuda') }} valor={tarifa} aoMudar={(v) => setTarifa(v as number | null)} />
        <div className="flex min-w-0 flex-col justify-end rounded-lg border border-borda bg-sup-2 px-3 py-2">
          <span className="kicker">{receber ? t('baixa.totalReceber') : t('baixa.totalPagar')}</span>
          <TextoCortado className={`numero text-xl font-semibold ${receber ? 'text-verde' : 'text-perigo'}`}>{dinheiro(Math.max(total, 0), doc.moeda)}</TextoCortado>
        </div>
        {erro && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo sm:col-span-2">
            {erro}
          </p>
        )}
      </div>
    </Modal>
  );
}
